import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Dumbbell, LogOut, CalendarCheck, Wallet, Award, Gift, Apple, Activity } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => "₹" + (n || 0).toLocaleString("en-IN");

export default function MemberPortal() {
  const [data, setData] = useState(null);
  const [authed, setAuthed] = useState(false);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("phone");
  const [mockOtp, setMockOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const loadDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/member/dashboard`);
      setData(res.data); setAuthed(true);
    } catch (e) { setAuthed(false); }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const requestOtp = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/member/request-otp`, { phone });
      setMockOtp(res.data.mock_otp); setStep("otp");
    } catch (err) { setError(err.response?.data?.detail || "Failed"); } finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/member/verify-otp`, { phone, otp });
      loadDashboard();
    } catch (err) { setError(err.response?.data?.detail || "Invalid OTP"); } finally { setLoading(false); }
  };

  const logout = async () => { await axios.post(`${BACKEND_URL}/api/member/logout`); setAuthed(false); setData(null); setStep("phone"); setPhone(""); setOtp(""); };

  if (!authed) {
    return (
      <div className="min-h-screen bg-main_bg flex items-center justify-center p-6 admin-scope">
        <div className="bg-card_bg border border-border_soft rounded-2xl p-8 w-full max-w-md">
          <div className="flex flex-col items-center mb-6">
            <div className="bg-gold/15 text-gold p-4 rounded-2xl mb-3"><Dumbbell size={32} /></div>
            <h1 className="text-3xl font-serif text-text_primary">Member Portal</h1>
            <p className="text-text_secondary text-sm">SVT Golden Fitness</p>
          </div>
          {error && <div className="text-accent_danger text-sm bg-accent_danger/10 border border-accent_danger/20 px-4 py-2.5 rounded-xl mb-4">{error}</div>}
          {step === "phone" ? (
            <form onSubmit={requestOtp} className="space-y-4">
              <div>
                <label className="text-xs text-text_secondary uppercase tracking-wider font-semibold mb-1.5 block">Registered Phone</label>
                <input data-testid="member-phone-input" required type="text" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Your phone number" className="w-full bg-main_bg border border-border_soft rounded-xl p-3.5 text-text_primary" />
              </div>
              <button data-testid="member-send-otp-btn" disabled={loading} type="submit" className="w-full bg-primary text-[#111827] font-bold py-3.5 rounded-xl hover:bg-primary_hover transition-colors disabled:opacity-50">{loading ? "Sending..." : "Send OTP"}</button>
            </form>
          ) : (
            <form onSubmit={verifyOtp} className="space-y-4">
              {mockOtp && <div className="text-xs text-gold bg-gold/10 border border-gold/20 px-4 py-2.5 rounded-xl">Demo OTP (SMS not configured): <b>{mockOtp}</b></div>}
              <div>
                <label className="text-xs text-text_secondary uppercase tracking-wider font-semibold mb-1.5 block">Enter OTP</label>
                <input data-testid="member-otp-input" required type="text" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code" className="w-full bg-main_bg border border-border_soft rounded-xl p-3.5 text-text_primary tracking-widest" />
              </div>
              <button data-testid="member-verify-otp-btn" disabled={loading} type="submit" className="w-full bg-primary text-[#111827] font-bold py-3.5 rounded-xl hover:bg-primary_hover transition-colors disabled:opacity-50">{loading ? "Verifying..." : "Verify & Login"}</button>
            </form>
          )}
        </div>
      </div>
    );
  }

  const m = data.member;
  return (
    <div data-testid="member-portal" className="min-h-screen bg-main_bg admin-scope text-text_primary">
      <header className="bg-sidebar_bg border-b border-border_soft px-6 py-4 flex justify-between items-center">
        <div className="flex items-center gap-3"><div className="bg-gold/15 text-gold p-2 rounded-xl"><Dumbbell size={22} /></div><div><p className="font-bold">{m.name}</p><p className="text-xs text-text_secondary">{m.id}</p></div></div>
        <button data-testid="member-logout-btn" onClick={logout} className="text-text_secondary hover:text-accent_danger flex items-center gap-2 text-sm"><LogOut size={16} /> Logout</button>
      </header>

      <div className="p-6 max-w-5xl mx-auto space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="bg-card_bg border border-border_soft rounded-xl p-6 md:col-span-2">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-text_secondary uppercase tracking-wider">Membership</p>
                <h2 className="text-2xl font-bold mt-1">{m.package || "No package"}</h2>
                <p className="text-sm text-text_secondary mt-1">Expires {m.expiry_date}</p>
                <span className={`inline-block mt-3 px-3 py-1 rounded-full text-xs font-bold ${data.days_left > 0 ? "bg-accent_success/15 text-accent_success" : "bg-accent_danger/15 text-accent_danger"}`}>{data.days_left} days left</span>
              </div>
              <div className="bg-white p-2 rounded-lg"><QRCodeSVG value={m.id} size={84} /></div>
            </div>
          </div>
          <div className="bg-card_bg border border-border_soft rounded-xl p-6 flex flex-col justify-center gap-4">
            <Stat icon={Award} label="PT Sessions Left" value={m.pt_sessions_remaining} />
            <Stat icon={CalendarCheck} label="Total Check-ins" value={data.attendance_count} />
            <Stat icon={Gift} label="Referrals" value={data.referrals} />
          </div>
        </div>

        <div className="bg-card_bg border border-border_soft rounded-xl p-6">
          <p className="text-xs text-text_secondary uppercase tracking-wider mb-1">Your Referral Code</p>
          <div className="flex items-center gap-3"><span className="text-2xl font-bold text-gold font-mono">{m.referral_code}</span><span className="text-sm text-text_secondary">Share with friends to earn rewards!</span></div>
        </div>

        {data.bmi_history?.length > 0 && (
          <div className="bg-card_bg border border-border_soft rounded-xl p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><Activity size={18} className="text-gold" /> BMI Progress</h3>
            <div className="h-52"><ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.bmi_history}>
                <XAxis dataKey="date" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#94A3B8" fontSize={11} domain={["auto", "auto"]} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ backgroundColor: "#131A26", border: "1px solid #243047", borderRadius: 8, color: "#fff" }} />
                <Line type="monotone" dataKey="value" stroke="#FACC15" strokeWidth={3} dot={{ r: 4, fill: "#FACC15" }} />
              </LineChart>
            </ResponsiveContainer></div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <PlanList icon={Dumbbell} title="Workout Plans" plans={data.workout_plans} render={(p) => `${(p.days || []).length} exercises`} />
          <PlanList icon={Apple} title="Diet Plans" plans={data.diet_plans} render={(p) => `${(p.meals || []).length} meals`} />
        </div>

        <div className="bg-card_bg border border-border_soft rounded-xl p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2"><Wallet size={18} className="text-gold" /> Payment History</h3>
          <div className="space-y-2">
            {data.invoices?.length === 0 ? <p className="text-sm text-text_secondary/70 italic">No invoices yet.</p> : data.invoices.map((i) => (
              <div key={i.id} className="flex justify-between items-center bg-main_bg border border-border_soft/60 rounded-xl px-4 py-3 text-sm">
                <div><span className="font-mono text-text_secondary text-xs">{i.id}</span><p className="text-text_primary">{i.type}</p></div>
                <div className="text-right"><span className="font-bold">{fmt(i.amount)}</span><p className={`text-xs ${i.status === "Paid" ? "text-accent_success" : "text-accent_warning"}`}>{i.status}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3"><div className="bg-gold/10 text-gold p-2 rounded-lg"><Icon size={18} /></div><div><p className="text-xs text-text_secondary">{label}</p><p className="text-lg font-bold">{value ?? 0}</p></div></div>
);

const PlanList = ({ icon: Icon, title, plans, render }) => (
  <div className="bg-card_bg border border-border_soft rounded-xl p-6">
    <h3 className="font-semibold mb-4 flex items-center gap-2"><Icon size={18} className="text-gold" /> {title}</h3>
    <div className="space-y-2">
      {(!plans || plans.length === 0) ? <p className="text-sm text-text_secondary/70 italic">No plans assigned yet.</p> : plans.map((p) => (
        <div key={p.id} className="bg-main_bg border border-border_soft/60 rounded-xl px-4 py-3 flex justify-between"><span className="text-text_primary text-sm font-medium">{p.name}</span><span className="text-xs text-text_secondary">{render(p)}</span></div>
      ))}
    </div>
  </div>
);
